import { useCallback, useMemo, useRef, useState } from "react"
import type { MouseEvent } from "react"
import { VisibilityOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Card, Chip, CircularProgress, Typography } from "@mui/material"
import { isEqual } from "lodash"
import { log } from "helpers/logger"
import { useSkillCatalog } from "resources/skills"
import { isScheduledWorkflowNotFound, SCHEDULED_WORKFLOW_ERROR_CODES, scheduledWorkflowErrorOf } from "resources/scheduled-workflows/errors"
import type { ScheduledWorkflowErrorCode } from "resources/scheduled-workflows/errors"
import { useScheduledWorkflow, useUpdateScheduledWorkflow } from "resources/scheduled-workflows/hooks"
import type { UpdateWorkflowDraft } from "resources/scheduled-workflows/draft-contract"
import type { ScheduledWorkflow, ScheduledWorkflowSchedule, UpdateScheduledWorkflowPayload } from "resources/scheduled-workflows/types"
import { computeNextRunAt, emailResultsLine, formatSchedule, formatScheduleDays, viewerTimeZone } from "../scheduled-workflows/schedule-format"
import { NO_NOTIFICATION, WORKFLOW_GONE, inputUnprocessableMessage, requestValidationMessage, skillsReadOnlyMessage, skillsUnusableMessage } from "./create-draft-failure"
import { workflowDraftArtifactId } from "./artifact-contract"
import type { MarkdownArtifact } from "./artifact-contract"
import { ACTIONS_SX, CARD_SX, HEAD_SX, LINES_SX, NAME_SX, PREVIEW_SX } from "./card-styles"
import { useArtifactPanelActions } from "./use-artifact-panel"

const MANUAL_ONLY_SCHEDULE = "Runs only when you run it"
const SKILLS_REFRESH_NOTE = "Applying this also updates the workflow's skills to their latest content."

type ChangedFields = Partial<Omit<UpdateScheduledWorkflowPayload, "configVersion">>

type UpdateFailure =
  | { kind: "warning", testId: string, message: string }
  | { kind: "error" }

interface Applied {
  message: string
  lines: string[]
  isEmailOnly: boolean
}

function isSameSet(first: string[], second: string[] | undefined): boolean {
  if (second === undefined) return false
  const firstSet = new Set(first)
  const secondSet = new Set(second)
  return firstSet.size === secondSet.size && [...firstSet].every((skillId) => secondSet.has(skillId))
}

function withSortedDays(schedule: ScheduledWorkflowSchedule | undefined): ScheduledWorkflowSchedule | undefined {
  return schedule == null ? schedule : { ...schedule, days: [...schedule.days].sort() }
}

function changedFieldsOf(payload: UpdateWorkflowDraft["payload"], current: ScheduledWorkflow | undefined): ChangedFields {
  const changed: ChangedFields = {}
  if (payload.name !== undefined && payload.name !== current?.name) changed.name = payload.name
  if (payload.description !== undefined && payload.description !== current?.description) changed.description = payload.description
  if (payload.initialMessage !== undefined && payload.initialMessage !== current?.initialMessage) changed.initialMessage = payload.initialMessage
  if (payload.skills !== undefined && !isSameSet(payload.skills, current?.skills)) changed.skills = payload.skills
  if (payload.schedule !== undefined && !isEqual(withSortedDays(payload.schedule), withSortedDays(current?.schedule))) changed.schedule = payload.schedule
  if (payload.emailResults !== undefined && payload.emailResults !== current?.emailResults) changed.emailResults = payload.emailResults
  return changed
}

function scheduleText(schedule: ScheduledWorkflowSchedule, now: Date): string {
  if (schedule == null) return MANUAL_ONLY_SCHEDULE
  try {
    return formatSchedule(schedule, viewerTimeZone(), { now, nextRunAt: computeNextRunAt(schedule, now) })
  } catch (error) {
    log(error)
    return formatScheduleDays(schedule)
  }
}

function skillsLine(skills: string[], currentSkills: string[] | undefined, skillName: (skillId: string) => string): string {
  if (currentSkills === undefined) return "Skills changed"
  const added = skills.filter((skillId) => !currentSkills.includes(skillId)).map(skillName)
  const removed = currentSkills.filter((skillId) => !skills.includes(skillId)).map(skillName)
  const parts = [added.length > 0 ? `+ ${added.join(", ")}` : null, removed.length > 0 ? `− ${removed.join(", ")}` : null]
  return `Skills: ${parts.filter((part) => part !== null).join(", ")}`
}

function changeLines(changed: ChangedFields, current: ScheduledWorkflow | undefined, now: Date, skillName: (skillId: string) => string): string[] {
  const lines: string[] = []
  if (changed.schedule !== undefined) {
    const newText = scheduleText(changed.schedule, now)
    lines.push(current === undefined ? `Schedule: ${newText}` : `Schedule: ${scheduleText(current.schedule, now)} → ${newText}`)
  }
  if (changed.name !== undefined) lines.push(current === undefined ? `Name: ${changed.name}` : `Renamed from ${current.name}`)
  if (changed.initialMessage !== undefined) lines.push("Instructions changed")
  if (changed.description !== undefined) lines.push("Description changed")
  if (changed.skills !== undefined) lines.push(skillsLine(changed.skills, current?.skills, skillName))
  if (changed.emailResults !== undefined) lines.push(emailResultsLine(changed.emailResults))
  return lines
}

function appliedMessage(workflow: ScheduledWorkflow, name: string, isEmailOnly: boolean): string {
  if (isEmailOnly) return "This setting applies now."
  if (workflow.status === "paused" || workflow.blockedReason != null) return `"${name}" stays paused. Changes apply once it runs again.`
  return "Changes apply from the next run."
}

function warning(code: ScheduledWorkflowErrorCode, message: string): UpdateFailure {
  const testId = `kelly-workflow-draft-${code.replace("SCHEDULED_WORKFLOW_", "").toLowerCase().replace(/_/g, "-")}`
  return { kind: "warning", testId, message }
}

function updateFailureOf(error: unknown, name: string, skillName: (skillId: string) => string): UpdateFailure {
  log(error)
  const workflowError = scheduledWorkflowErrorOf(error)
  switch (workflowError?.code) {
    case SCHEDULED_WORKFLOW_ERROR_CODES.configVersionConflict:
      return warning(workflowError.code, "This workflow changed since Kelly drafted this. Ask Kelly again.")
    case SCHEDULED_WORKFLOW_ERROR_CODES.idNotFound:
      return warning(workflowError.code, WORKFLOW_GONE)
    case SCHEDULED_WORKFLOW_ERROR_CODES.patAuthorizationError:
      return warning(workflowError.code, "Edits are refused while API tokens are off. An admin can turn them back on.")
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsUnresolved:
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsDuplicate:
      return warning(workflowError.code, skillsUnusableMessage(workflowError.details.skills.map(skillName)))
    case SCHEDULED_WORKFLOW_ERROR_CODES.skillsWriteAccess:
      return warning(workflowError.code, skillsReadOnlyMessage(workflowError.details.skills.map(skillName)))
    case SCHEDULED_WORKFLOW_ERROR_CODES.inputUnprocessableEntity:
      return warning(workflowError.code, inputUnprocessableMessage(name, workflowError.details.field))
    case SCHEDULED_WORKFLOW_ERROR_CODES.requestValidationUnprocessableEntity:
      return warning(workflowError.code, requestValidationMessage(name))
    case SCHEDULED_WORKFLOW_ERROR_CODES.updateArchived:
      return warning(workflowError.code, "This workflow is archived.")
    default:
      return { kind: "error" }
  }
}

function instructionsArtifact(draftId: string, title: string, initialMessage: string, skillNames: string[]): MarkdownArtifact {
  const skills = skillNames.length === 0 ? [] : ["**Skills**", skillNames.map((skillName) => `- ${skillName}`).join("\n")]
  return { id: workflowDraftArtifactId(draftId), type: "markdown", title, markdown: [initialMessage, ...skills].join("\n\n") }
}

export interface UpdateDraftCardProps {
  draft: UpdateWorkflowDraft
}

export function UpdateDraftCard({ draft }: UpdateDraftCardProps) {
  const { payload } = draft
  const { mutateAsync, isPending } = useUpdateScheduledWorkflow(NO_NOTIFICATION)
  const { data: current, error: readError } = useScheduledWorkflow(draft.scheduledWorkflowId)
  const { sections } = useSkillCatalog()
  const panel = useArtifactPanelActions()
  const [now] = useState(() => new Date())
  const [applied, setApplied] = useState<Applied | null>(null)
  const [failure, setFailure] = useState<UpdateFailure | null>(null)
  const isRunning = useRef(false)

  const catalogNames = useMemo(() => new Map(sections.flatMap((section) => section.skills.flatMap((skill) => [
    [skill.id, skill.name] as const,
    ...(skill.plugin == null ? [] : [[`${skill.plugin}/${skill.id}`, skill.name] as const]),
  ]))), [sections])
  const skillName = useCallback((skillId: string) => catalogNames.get(skillId) ?? skillId, [catalogNames])

  const isReadMissing = readError != null && isScheduledWorkflowNotFound(readError)
  const isReady = current !== undefined || (readError != null && !isReadMissing)
  const name = payload.name ?? current?.name ?? "Workflow"
  const changed = useMemo(() => (isReady ? changedFieldsOf(payload, current) : {}), [isReady, payload, current])
  const lines = useMemo(() => changeLines(changed, current, now, skillName), [changed, current, now, skillName])
  const changedKeys = Object.keys(changed)
  const payloadKeys = Object.entries(payload).filter(([key, value]) => key !== "configVersion" && value !== undefined).map(([key]) => key)
  const liveKeys = isReady ? changedKeys : payloadKeys
  const isEmailOnly = applied?.isEmailOnly ?? (liveKeys.length === 1 && liveKeys[0] === "emailResults")
  const isAlreadyApplied = isReady && applied === null && changedKeys.length === 0 && !isPending && failure === null
  const previewSkillNames = useMemo(() => (payload.skills ?? current?.skills ?? []).map(skillName), [payload.skills, current?.skills, skillName])

  const onPreview = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (payload.initialMessage === undefined) return
    panel?.openArtifact(instructionsArtifact(draft.draftId, name, payload.initialMessage, previewSkillNames), event.currentTarget, name)
  }, [panel, draft.draftId, name, payload.initialMessage, previewSkillNames])

  const onApply = useCallback(async () => {
    if (isRunning.current) return
    isRunning.current = true
    setFailure(null)
    try {
      const workflow = await mutateAsync({ scheduledWorkflowId: draft.scheduledWorkflowId, payload: { configVersion: draft.configVersion, ...changed } })
      setApplied({ message: appliedMessage(workflow, name, isEmailOnly), lines, isEmailOnly })
    } catch (error) {
      setFailure(updateFailureOf(error, name, skillName))
    } finally {
      isRunning.current = false
    }
  }, [mutateAsync, draft.scheduledWorkflowId, draft.configVersion, changed, name, isEmailOnly, lines, skillName])

  const onConfirm = useCallback(() => { void onApply() }, [onApply])

  const isArchived = current?.archivedAt != null
  const shownFailure = isReadMissing
    ? warning(SCHEDULED_WORKFLOW_ERROR_CODES.idNotFound, WORKFLOW_GONE)
    : isArchived ? warning(SCHEDULED_WORKFLOW_ERROR_CODES.updateArchived, "This workflow is archived.") : failure
  const warningFailure = shownFailure?.kind === "warning" ? shownFailure : null
  const shownLines = applied?.lines ?? lines

  return (
    <Card variant="outlined" sx={CARD_SX} data-testid="kelly-workflow-draft">
      <Box sx={HEAD_SX}>
        <Typography sx={NAME_SX} data-testid="kelly-workflow-draft-name">{name}</Typography>
        <Chip size="small" variant="outlined" color="warning" label="Changes an existing workflow" data-testid="kelly-workflow-draft-action-chip" />
        {panel !== null && payload.initialMessage !== undefined && (
          <Button
            size="small"
            variant="text"
            sx={PREVIEW_SX}
            startIcon={<VisibilityOutlined fontSize="small" />}
            onClick={onPreview}
            data-testid="kelly-workflow-draft-preview"
          >
            Preview instructions
          </Button>
        )}
      </Box>
      {shownLines.length > 0 && (
        <Box component="ul" sx={LINES_SX}>
          {shownLines.map((line) => <Typography key={line} component="li" variant="body2">{line}</Typography>)}
        </Box>
      )}
      {!isEmailOnly && !isAlreadyApplied && applied === null && warningFailure === null && <Typography variant="body2" color="text.secondary">{SKILLS_REFRESH_NOTE}</Typography>}
      {applied !== null ? (
        <Alert severity="success" data-testid="kelly-workflow-draft-done">{applied.message}</Alert>
      ) : (
        <>
          {warningFailure !== null && <Alert severity="warning" data-testid={warningFailure.testId}>{warningFailure.message}</Alert>}
          {shownFailure?.kind === "error" && (
            <Alert severity="error" data-testid="kelly-workflow-draft-error">{`"${name}" wasn't saved. Try again.`}</Alert>
          )}
          {warningFailure === null && isAlreadyApplied && (
            <Alert severity="info" data-testid="kelly-workflow-draft-done">This workflow already has these changes.</Alert>
          )}
          {warningFailure === null && !isAlreadyApplied && (
            <Box sx={ACTIONS_SX}>
              <Button
                variant="contained"
                size="small"
                disabled={isPending || !isReady}
                onClick={onConfirm}
                data-testid="kelly-workflow-draft-confirm"
                startIcon={isPending ? <CircularProgress size={14} /> : undefined}
              >
                Apply changes
              </Button>
            </Box>
          )}
        </>
      )}
    </Card>
  )
}
